import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCapabilities } from '../hooks/useCapabilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Play, Plus, RefreshCw, RotateCw, Square } from 'lucide-react';
import {
  fetchPrototype,
  fetchPrototypePulls,
  fetchWorkspaceEntitlements,
  mergePrototypePr,
  redeployPrototype,
  restartPrototype,
  startPrototype,
  stopPrototype,
  wakePrototype,
} from '../api';
import { useOrg } from '../org/useOrg';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Callout } from '../components/Callout';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { OverflowMenu, type OverflowMenuItem } from '../components/OverflowMenu';
import { RunTaskModal } from '../components/RunTaskModal';
import { IssuesPanel } from '../components/IssuesPanel';
import { PageHeader } from '../components/PageHeader';
import { PrototypeDeployProgress } from '../components/PrototypeDeployProgress';
import { PrototypeFailedBuildOutput } from '../components/PrototypeBuildOutput';
import { PrototypeInspectPanel } from '../components/PrototypeInspectPanel';
import { PrototypeLogsPanel } from '../components/PrototypeLogsPanel';
import { PrototypeSchemaCard } from '../components/PrototypeSchemaCard';
import { PrototypeApiCard } from '../components/PrototypeApiCard';
import { PrototypeStatsBar } from '../components/PrototypeStatsBar';
import { Row, RowList } from '../components/Row';
import { UnderlineTabs } from '../components/Tabs';
import { useBoardDeepLink } from '../hooks/useBoardDeepLink';
import { Spinner } from '../components/Spinner';
import { ErrorState, LoadingState } from '../components/States';
import { ProjectSectionNav } from '../components/ProjectSectionNav';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';

// Statuses that are still in flight — poll so the deploy badge advances
// without a manual refresh, same convention as PrototypesPage.
const IN_FLIGHT_STATUSES = new Set(['provisioning', 'building']);

export function PrototypeDetailPage() {
  const { id = '' } = useParams();
  const { wp } = useWorkspacePath();
  // Members can view a prototype and file issues, but not run tasks, drive the
  // deploy lifecycle, merge PRs, edit its configuration, or delete it.
  const { can } = useCapabilities();
  const canManage = can('prototype:manage');
  const canRunTask = can('run:trigger');
  const canMergePr = can('pr:merge');
  const queryClient = useQueryClient();
  const [confirmRedeploy, setConfirmRedeploy] = useState(false);
  const [confirmMergePr, setConfirmMergePr] = useState<{ number: number; title: string } | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [inspectOpen, setInspectOpen] = useState(false);
  // The rich deploy progress card is opt-in: collapsed behind the status pill.
  const [buildOpen, setBuildOpen] = useState(false);
  const [runTaskOpen, setRunTaskOpen] = useState(false);
  const [bulkSelecting, setBulkSelecting] = useState(false);
  const isMobile = useIsMobile();
  const protoQuery = useQuery({
    queryKey: qk.prototype(id),
    queryFn: () => fetchPrototype(id),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data &&
      IN_FLIGHT_STATUSES.has(query.state.data.deployment.deployStatus)
        ? 3000
        : false,
  });

  // Collapse the build card once the deploy settles, so the next deploy starts closed.
  const currentDeployStatus = protoQuery.data?.deployment.deployStatus;
  useEffect(() => {
    if (!currentDeployStatus || !IN_FLIGHT_STATUSES.has(currentDeployStatus)) setBuildOpen(false);
  }, [currentDeployStatus]);

  // Keep the URL on the pretty code ("/prototypes/OMHB"), never the UUID.
  useCanonicalProjectRef(id, protoQuery.data?.code);
  // All data + mutations key off the resolved UUID; only the top-level fetch
  // (and its cache key) use the URL param, since the UUID isn't known before it.
  const pid = protoQuery.data?.id;

  const pullsQuery = useQuery({
    queryKey: qk.prototypePulls(pid ?? id),
    queryFn: () => fetchPrototypePulls(pid!),
    enabled: !!pid,
  });

  // On the shared credential rail (basic plan) prototype PRs auto-merge and the
  // preview redeploys automatically — the GitHub PR/merge machinery is noise for
  // a non-technical customer, so hide the panel there. Pro (byo) keeps it.
  const { activeOrg } = useOrg();
  const entitlementsQuery = useQuery({
    queryKey: ['orgEntitlements', activeOrg?.id],
    queryFn: () => fetchWorkspaceEntitlements(activeOrg!.id),
    enabled: !!activeOrg?.id,
  });
  // Basic (shared-rail) prototypes live under the platform's dedicated GitHub
  // org, which the customer can't access — hide the PR panel and the repo link.
  const isBasic = entitlementsQuery.data?.credentialMode === 'shared';
  const hidePrPanel = isBasic;
  // Custom resource limits are a Pro-plan feature; hide the card on basic. The server
  // enforces this too — this just keeps the control out of a basic customer's UI.
  useDocumentTitle(protoQuery.data?.name);

  // The tab belongs to one prototype: switching prototype lands on Issues again.
  type ProtoTab = 'issues' | 'prs' | 'schema' | 'api';
  const tabKey = pid ?? id;
  const [tabState, setTabState] = useState<{ key: string; tab: ProtoTab }>({ key: tabKey, tab: 'issues' });
  const tab: ProtoTab = tabState.key === tabKey ? tabState.tab : 'issues';
  const setTab = (t: ProtoTab) => setTabState({ key: tabKey, tab: t });
  const tabsRef = useRef<HTMLDivElement>(null);
  const showTab = (t: ProtoTab) => {
    setTab(t);
    tabsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  // ?issues=<state> (Inbox, Needs-you links) opens the board on that filter.
  const board = useBoardDeepLink(!!protoQuery.data, () => showTab('issues'));

  const invalidateProto = () =>
    queryClient.invalidateQueries({ queryKey: qk.prototype(id) });

  const redeployMutation = useMutation({
    mutationFn: () => redeployPrototype(pid!),
    onSuccess: () => {
      setConfirmRedeploy(false);
      invalidateProto();
      // A redeploy advances the deployed commit, which is the schema the DB
      // diagram reads from — refresh it (and the API view) too.
      queryClient.invalidateQueries({ queryKey: qk.prototypeSchema(pid!) });
      queryClient.invalidateQueries({ queryKey: qk.prototypeApiSpec(pid!) });
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => stopPrototype(pid!),
    onSuccess: invalidateProto,
  });

  const startMutation = useMutation({
    mutationFn: () => startPrototype(pid!),
    onSuccess: invalidateProto,
  });

  const restartMutation = useMutation({
    mutationFn: () => restartPrototype(pid!),
    onSuccess: invalidateProto,
  });

  const wakeMutation = useMutation({
    mutationFn: () => wakePrototype(pid!),
    onSuccess: invalidateProto,
  });

  const mergeMutation = useMutation({
    mutationFn: (prNumber: number) => mergePrototypePr(pid!, prNumber),
    onSuccess: () => {
      setConfirmMergePr(null);
      invalidateProto();
      queryClient.invalidateQueries({ queryKey: qk.prototypePulls(pid!) });
      // Refresh the issues list so the merged run's PR badge flips to "Merged".
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(pid!) });
      // A merge triggers a redeploy from the new main — the DB diagram will
      // pick up any schema change on the next read.
      queryClient.invalidateQueries({ queryKey: qk.prototypeSchema(pid!) });
      queryClient.invalidateQueries({ queryKey: qk.prototypeApiSpec(pid!) });
    },
  });

  if (protoQuery.isLoading) return <LoadingState label="Loading prototype…" />;
  if (protoQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(protoQuery.error, 'Failed to load prototype')}
        onRetry={() => protoQuery.refetch()}
      />
    );
  }

  const proto = protoQuery.data!;
  // The handle to use in URLs — the pretty code when present, else the UUID.
  const ref = proto.code ?? proto.id;
  const { deployment } = proto;
  const platformDisplay = deployment.platformUrl.replace(/^https?:\/\//, '');
  // Prefer a verified custom domain as the headline URL for quick navigation;
  // fall back to the platform URL. The platform URL stays reachable as a
  // secondary link and remains the CNAME target shown in the domains card.
  const activeDomain = (deployment.domains ?? []).find((d) => d.status === 'active');
  const primaryUrl = activeDomain ? `https://${activeDomain.domain}` : deployment.liveUrl;
  const primaryDisplay = primaryUrl.replace(/^https?:\/\//, '');
  const pulls = pullsQuery.data ?? [];
  const actionError =
    stopMutation.error ??
    startMutation.error ??
    restartMutation.error ??
    wakeMutation.error;

  // Deploy-lifecycle actions live behind the header "⋯" menu so the action row
  // keeps a single primary button (New issue). Items are state-dependent.
  const menuItems: OverflowMenuItem[] = [
    // On phones the header keeps only this menu; "Run a task" loses its
    // inline button there and moves in here instead.
    ...(isMobile && canRunTask
      ? [
          {
            label: 'Run a task',
            icon: <Play className="h-3.5 w-3.5" aria-hidden="true" />,
            onClick: () => setRunTaskOpen(true),
          },
        ]
      : []),
  ];
  // Waking an idle-slept prototype is open to contributors too (prototype:operate).
  if (deployment.deployStatus === 'sleeping' && can('prototype:operate', { projectId: proto.id })) {
    menuItems.push({
      label: 'Wake',
      icon: wakeMutation.isPending ? (
        <Spinner className="h-3.5 w-3.5" />
      ) : (
        <Play className="h-3.5 w-3.5" aria-hidden="true" />
      ),
      onClick: () => wakeMutation.mutate(),
      disabled: wakeMutation.isPending,
    });
  }
  // Deploy-lifecycle actions are owner-only; a member's menu keeps only "Runs".
  if (canManage) {
    if (deployment.deployStatus === 'sleeping') {
      // Stopped stays down: a visit no longer wakes it.
      menuItems.push({
        label: 'Stop',
        icon: stopMutation.isPending ? (
          <Spinner className="h-3.5 w-3.5" />
        ) : (
          <Square className="h-3.5 w-3.5" aria-hidden="true" />
        ),
        onClick: () => stopMutation.mutate(),
        disabled: stopMutation.isPending,
      });
    }
    if (deployment.deployStatus === 'live') {
      menuItems.push({
        label: 'Stop',
        icon: stopMutation.isPending ? (
          <Spinner className="h-3.5 w-3.5" />
        ) : (
          <Square className="h-3.5 w-3.5" aria-hidden="true" />
        ),
        onClick: () => stopMutation.mutate(),
        disabled: stopMutation.isPending,
      });
      menuItems.push({
        label: 'Restart',
        icon: restartMutation.isPending ? (
          <Spinner className="h-3.5 w-3.5" />
        ) : (
          <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
        ),
        onClick: () => restartMutation.mutate(),
        disabled: restartMutation.isPending,
      });
    }
    if (deployment.deployStatus === 'stopped') {
      menuItems.push({
        label: 'Start',
        icon: startMutation.isPending ? (
          <Spinner className="h-3.5 w-3.5" />
        ) : (
          <Play className="h-3.5 w-3.5" aria-hidden="true" />
        ),
        onClick: () => startMutation.mutate(),
        disabled: startMutation.isPending,
      });
    }
    menuItems.push({
      label: 'Redeploy',
      icon: redeployMutation.isPending ? (
        <Spinner className="h-3.5 w-3.5" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
      ),
      onClick: () => setConfirmRedeploy(true),
      disabled: redeployMutation.isPending,
    });
  }

  return (
    <div className="space-y-6 pb-16 sm:pb-0">
      <Breadcrumbs
        items={[{ label: 'Prototypes', to: wp('/prototypes') }, { label: proto.name }]}
      />

      <PageHeader
        stackActions
        title={
          proto.code ? (
            <>
              <span className="mr-2 font-mono text-base font-medium uppercase tracking-wide text-ink-faint">
                {proto.code}
              </span>
              {proto.name}
            </>
          ) : (
            proto.name
          )
        }
        subtitle={
          <span className="flex min-w-0 flex-col gap-y-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
            {primaryUrl && (
              <a
                href={primaryUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 font-mono text-signal hover:text-signal"
              >
                <span className="truncate">{primaryDisplay}</span>
                <ExternalLink className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
              </a>
            )}
            {deployment.platformUrl !== primaryUrl && (
              <a
                href={deployment.platformUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 font-mono hover:text-ink"
              >
                <span className="truncate">{platformDisplay}</span>
                <ExternalLink className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
              </a>
            )}
            {!isBasic && (
              <a
                href={proto.repoUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 hover:text-ink"
              >
                <span className="truncate">{proto.repoFullName}</span>
                <ExternalLink className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
              </a>
            )}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Section navigation lives in ProjectSectionNav below the header. */}
            {canRunTask && (
              <button
                type="button"
                className="btn-secondary hidden sm:inline-flex"
                onClick={() => setRunTaskOpen(true)}
              >
                <Play className="h-3.5 w-3.5" aria-hidden="true" />
                Run a task
              </button>
            )}
            {can('issue:create', { projectId: proto.id }) && (
              <Link
                to={wp(`/prototypes/${ref}/issues/new`)}
                className="btn-primary hidden sm:inline-flex"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                New issue
              </Link>
            )}
            <OverflowMenu items={menuItems} label="Deploy actions" />
          </div>
        }
      />

      <ProjectSectionNav
        kind="prototype"
        projectRef={ref}
        active="overview"
        newIssueTo={
          !bulkSelecting && can('issue:create', { projectId: proto.id })
            ? wp(`/prototypes/${ref}/issues/new`)
            : undefined
        }
      />

      <PrototypeStatsBar
        prototypeId={proto.id}
        deployStatus={deployment.deployStatus}
        lastRequestAt={deployment.lastRequestAt}
        logsOpen={logsOpen}
        onToggleLogs={() => setLogsOpen((o) => !o)}
        inspectOpen={inspectOpen}
        onToggleInspect={() => setInspectOpen((o) => !o)}
        buildOpen={buildOpen}
        onToggleBuild={() => setBuildOpen((o) => !o)}
      />

      {buildOpen && (
        <div id="prototype-build-panel">
          <PrototypeDeployProgress
            prototypeId={proto.id}
            deployStatus={deployment.deployStatus}
            since={deployment.updatedAt}
          />
        </div>
      )}

      {logsOpen && (
        <div id="prototype-logs-panel">
          <PrototypeLogsPanel
            prototypeId={proto.id}
            deployStatus={deployment.deployStatus}
            autoStream
          />
        </div>
      )}

      {inspectOpen && (
        <div id="prototype-inspect-panel">
          <PrototypeInspectPanel prototypeId={proto.id} />
        </div>
      )}

      {deployment.deployStatus === 'failed' && deployment.lastError && (
        <Callout
          tone="danger"
          kicker="Deploy failed"
          detail={deployment.lastError ?? undefined}
        />
      )}

      {deployment.deployStatus === 'failed' && (
        <PrototypeFailedBuildOutput key={deployment.updatedAt} prototypeId={proto.id} />
      )}

      {actionError && (
        <Callout tone="danger">
          {errorMessage(actionError, 'Action failed')}
        </Callout>
      )}

      <RunTaskModal
        open={runTaskOpen}
        onClose={() => setRunTaskOpen(false)}
        projectId={proto.id}
      />

      <ConfirmDialog
        open={confirmRedeploy}
        title="Redeploy prototype?"
        confirmLabel="Redeploy prototype"
        busy={redeployMutation.isPending}
        error={
          redeployMutation.isError
            ? errorMessage(redeployMutation.error, 'Could not redeploy prototype')
            : null
        }
        onConfirm={() => redeployMutation.mutate()}
        onClose={() => {
          setConfirmRedeploy(false);
          redeployMutation.reset();
        }}
        message={
          <>
            Rebuild{' '}
            <span className="font-medium text-ink">{proto.name}</span>{' '}
            from the tip of <span className="font-mono">main</span> and swap the
            running container. Any in-flight build will be superseded.
          </>
        }
      />

      <ConfirmDialog
        open={confirmMergePr !== null}
        title="Merge pull request?"
        confirmLabel="Merge pull request"
        busy={mergeMutation.isPending}
        error={
          mergeMutation.isError
            ? errorMessage(mergeMutation.error, 'Could not merge pull request')
            : null
        }
        onConfirm={() => {
          if (confirmMergePr !== null) mergeMutation.mutate(confirmMergePr.number);
        }}
        onClose={() => {
          setConfirmMergePr(null);
          mergeMutation.reset();
        }}
        message={
          confirmMergePr && (
            <>
              Merge{' '}
              <span className="font-medium text-ink">
                #{confirmMergePr.number} {confirmMergePr.title}
              </span>{' '}
              into <span className="font-mono">main</span>? This will trigger a
              redeploy of the live prototype.
            </>
          )
        }
      />

      <div ref={tabsRef}>
        <UnderlineTabs
          ariaLabel="Prototype sections"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'issues', label: 'Issues' },
            ...(hidePrPanel ? [] : [{ value: 'prs' as const, label: `Pull requests · ${pulls.length}` }]),
            { value: 'schema', label: 'Schema' },
            { value: 'api', label: 'API' },
          ]}
        />
      </div>

      {tab === 'issues' && (
        <div>
          <h2 className="mb-3 text-sm font-semibold text-ink">Issues</h2>
          <IssuesPanel
            key={board.panelKey}
            initialStatus={board.status}
            projectId={proto.id}
            kind="prototype"
            projectRef={ref}
            onSelectionModeChange={setBulkSelecting}
          />
        </div>
      )}

      {tab === 'prs' && !hidePrPanel && (
        pulls.length > 0 ? (
          <div>
            <h2 className="mb-3 text-sm font-semibold text-ink">
              Open pull requests
            </h2>
            <RowList>
              {pulls.map((pr) => (
                <Row
                  key={pr.number}
                  href={pr.htmlUrl}
                  title={`#${pr.number} ${pr.title}`}
                  description={pr.headRef}
                  actions={
                    canMergePr ? (
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={() => setConfirmMergePr({ number: pr.number, title: pr.title })}
                        disabled={mergeMutation.isPending}
                      >
                        Merge
                      </button>
                    ) : undefined
                  }
                />
              ))}
            </RowList>
          </div>
        ) : (
          <p className="rounded-card border border-line bg-surface-2 px-4 py-6 text-center text-sm text-ink-muted">No open pull requests.</p>
        )
      )}

      {tab === 'schema' && <PrototypeSchemaCard prototypeId={proto.id} />}

      {tab === 'api' && <PrototypeApiCard prototypeId={proto.id} />}
    </div>
  );
}
